import { GUEST_COUNT_STRATEGY } from "@/enums";
import GuestCountRow from "@/components/guestCountRow/guestCountRow.vue";
import SetDefaultValuesModal from "@/components/setDefaultValuesModal/setDefaultValuesModal.vue";
import { SettingsService } from "@/services/settingsApi";
import moment from "moment";

export default {
	components: {
		GuestCountRow,
		SetDefaultValuesModal,
	},

	props: {
		serviceTimeSet: Object,
		guestCounts: Array,
		activeDate: String,
		showOnlyDefaults: Boolean,
	},

	data() {
		return {
			GUEST_COUNT_STRATEGY,

			showGlobalDefaultValuesModal: false,
			showMonthlyDefaultValuesModal: false,
			globalDefault: this.serviceTimeSet.global_default,
			monthlyDefault: this.serviceTimeSet.monthly_defaults[moment(this.activeDate).format("YYYY-MM")]
				? this.serviceTimeSet.monthly_defaults[moment(this.activeDate).format("YYYY-MM")]
				: null,
		};
	},

	computed: { guestCountStrategy },

	methods: {
		onUpdateGCValue,
		closeModal,
		updateDefaults,
		downloadGuestCounts,
		handleFileUpload,
		validateCSV,
		updateGuestCounts,
	},

	watch: {
		serviceTimeSet() {
			const monthlyDefaults = this.serviceTimeSet.monthly_defaults,
				month = moment(this.activeDate).format("YYYY-MM");

			this.monthlyDefault = monthlyDefaults[month];
			this.globalDefault = this.serviceTimeSet.global_default;
		},
	},
};

function guestCountStrategy() {
	return this.serviceTimeSet.gc_strategy;
}

function onUpdateGCValue(day, index, value) {
	this.$emit("update", day, index, value);
}

async function updateDefaults(target, values) {
	try {
		await SettingsService.setGuestCountDefaults({
			defaults:
				this.guestCountStrategy === GUEST_COUNT_STRATEGY.SERVICE_TIME
					? values.slice(0, this.serviceTimeSet.st_labels.length)
					: values.slice(-1),
			target: target,
			stsID: this.serviceTimeSet.id,
			date: moment(this.activeDate).set("date", 1).format("YYYY-MM-DD"),
		});

		target === "monthly" ? (this.monthlyDefault = values) : (this.globalDefault = values);

		this.closeModal();
	} catch (error) {
		this.$swal({
			title: this.$t("auth.attention"),
			text: this.$t("error.please_try_again"),
			type: "error",
			confirmButtonText: this.$t("auth.ok"),
		});
	}
}

function closeModal() {
	this.showMonthlyDefaultValuesModal = false;
	this.showGlobalDefaultValuesModal = false;
}

function downloadGuestCounts() {
	const month = moment(this.activeDate).set("date", 1).format("YYYY-MM-DD");
	SettingsService.downloadGuestCounts(month, this.serviceTimeSet.id).then((response) => {
		const url = window.URL.createObjectURL(new Blob([response.data])),
			link = document.createElement("a");

		link.href = url;
		link.setAttribute(
			"download",
			`${this.$t("settings.guest_count")}_${moment(this.activeDate).format("YYYY-MM")}_${this.serviceTimeSet.name}.csv`
		);
		document.body.appendChild(link);
		link.click();
	});
}

async function handleFileUpload(event) {
	const file = event.target.files[0];
	if (!file) {
		return;
	}

	const text = await file.text();
	const result = this.validateCSV(text);

	if (result.error) {
		this.$swal({
			html: result.error,
			customClass: "swal_modal",
		});
	} else {
		this.updateGuestCounts(result.parsedLines);
	}

	// Reset input so same file can be selected again
	event.target.value = "";
}

function validateCSV(text) {
	const numberOfValues =
		this.serviceTimeSet.gc_strategy === GUEST_COUNT_STRATEGY.SERVICE_TIME ? this.serviceTimeSet.st_labels.length : 1;

	// Parse CSV
	const lines = text
		.split("\n")
		.filter((line) => line.trim() !== "")
		.slice(1);

	const parsedLines = lines.map((line) => {
		const [_date, ..._values] = line.split(/[,;]/).map((value) => value.trim());
		return {
			date: moment(_date, "DD.MM.YYYY", true),
			values: _values,
		};
	});

	// Check if any line has invalid format
	if (
		parsedLines.some(
			({ date, values }) =>
				!date.isValid() ||
				values.length !== numberOfValues ||
				values.some((value) => {
					const num = Number(value);
					return !Number.isInteger(num) || num < 0;
				})
		)
	) {
		return { error: this.$t("guest_count.invalid_file_structure") };
	}

	// Check if any line has empty GC values
	if (parsedLines.some(({ values }) => values.some((value) => value === "" || value === null))) {
		return { error: this.$t("guest_count.empty_values") };
	}

	// Check if any line has date out of period (not in the month or not during the STS)
	if (
		parsedLines.some(
			({ date }) =>
				(this.serviceTimeSet.start_date && date < moment(this.serviceTimeSet.start_date)) ||
				(this.serviceTimeSet.end_date && date >= moment(this.serviceTimeSet.end_date)) ||
				this.activeDate.substring(0, 7) !== date.format("YYYY-MM")
		)
	) {
		return { error: this.$t("guest_count.out_of_period") };
	}

	// Convert values to integers after validation, and return parsed lines
	return {
		parsedLines: parsedLines.map(({ date, values }) => ({
			date,
			values: values.map((value) => parseInt(value, 10)),
		})),
	};
}

function updateGuestCounts(parsedLines) {
	for (const { date, values } of parsedLines) {
		const dateStr = date.format("YYYY-MM-DD");
		const gcRecord = this.guestCounts.find((record) => record.date === dateStr);

		if (!gcRecord || gcRecord.isClosed || gcRecord.isBeforeMeasStarted) {
			continue;
		}

		if (this.serviceTimeSet.gc_strategy === GUEST_COUNT_STRATEGY.SERVICE_TIME) {
			for (let index = 0; index < values.length; index++) {
				this.$emit("update", dateStr, index, values[index]);
			}
		} else {
			this.$emit("update", dateStr, 5, values[0]);
		}
	}
}
